import { useState, useEffect, useCallback, useRef } from 'react';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import PartnerLogo from '../../shared/components/PartnerLogo';
import { C, t } from '../../shared/constants/dashboardConstants';
import {
  getPartners, isPartnerApiUnavailable,
  PARTNER_TYPES, PARTNER_TYPE_LABEL, partnerTypes,
} from '../../api/partnerApi';

/** 한 번에 불러올 개수 */
const PAGE_SIZE = 24;

function PartnerCard({ partner, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', textAlign: 'left', padding: 0,
        border: `1px solid ${C.g200}`, borderRadius: '14px', overflow: 'hidden',
        background: C.white, cursor: 'pointer',
      }}
    >
      <div style={{
        width: '100%', aspectRatio: '4 / 3', background: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {/* 이름은 바로 아래에 크게 나오므로, 로고가 없으면 기본 아이콘만 둔다 */}
        <PartnerLogo src={partner.logoUrl} alt={partner.name} background={C.bg} />
      </div>

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* 제작사이면서 소속사인 업체가 있어 종류가 여러 개일 수 있다 */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {partnerTypes(partner).map(type => (
            <span
              key={type}
              style={{
                padding: '3px 8px', borderRadius: '6px',
                background: C.bg, border: `1px solid ${C.g200}`, ...t('11px', 500, C.sub),
              }}
            >
              {PARTNER_TYPE_LABEL[type]}
            </span>
          ))}
        </div>
        <span style={{ ...t('17px', 700, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {partner.name}
        </span>
        {partner.tagline && (
          <span style={{ ...t('14px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {partner.tagline}
          </span>
        )}
        {partner.summary && (
          <span style={t('13px', 400, C.g400)}>{partner.summary}</span>
        )}
      </div>
    </button>
  );
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [type, setType] = useState('ALL');
  const [query, setQuery] = useState('');
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  // 늦게 도착한 예전 응답이 최신 결과를 덮어쓰지 않도록
  const reqIdRef = useRef(0);
  const termRef = useRef('');

  /**
   * 한 번에 PAGE_SIZE개씩 불러온다.
   * append=true면 '더 보기' — 기존 목록 뒤에 이어 붙인다.
   */
  const fetchPartners = useCallback(async (t_, q, p = 1, append = false) => {
    const myReq = ++reqIdRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const { partners: list, total: count } = await getPartners({
        type: t_, q, page: p, limit: PAGE_SIZE,
      });
      if (myReq !== reqIdRef.current) return;
      setPartners(prev => (append ? [...prev, ...list] : list));
      setTotal(count);
      setPage(p);
      setUnavailable(false);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      if (isPartnerApiUnavailable(err)) { setUnavailable(true); setPartners([]); return; }
      const code = err.response?.status;
      setError(code ? `목록을 불러오지 못했습니다. (HTTP ${code})` : (err.message || '목록을 불러오지 못했습니다.'));
      // 더 보기 실패는 이미 본 목록까지 지우지 않는다
      if (!append) { setPartners([]); setTotal(0); }
    } finally {
      if (myReq !== reqIdRef.current) return;
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(type, termRef.current, 1); }, [type, fetchPartners]);

  const handleSearch = () => {
    termRef.current = query.trim();
    fetchPartners(type, termRef.current, 1);
  };

  const hasMore = partners.length < total;

  return (
    <PageLayout>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 style={{ ...t('36px', 700, C.bk), margin: 0 }}>파트너스</h1>
        <p style={{ ...t('16px', 400, C.sub), margin: '10px 0 32px' }}>
          캐스타와 함께하는 제작사, 소속사, 대학 등을 확인해보세요.
        </p>

        {/* 종류 필터 */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {PARTNER_TYPES.map(f => (
            <button
              key={f.key}
              onClick={() => setType(f.key)}
              style={{
                padding: '10px 20px', borderRadius: '10px', cursor: 'pointer',
                border: `1px solid ${type === f.key ? C.bk : C.g200}`,
                background: type === f.key ? C.bk : C.white,
                ...t('15px', 600, type === f.key ? C.white : C.bk),
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* 검색 */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '32px' }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="회사명, 학교명, 업종 검색 (Enter)"
            aria-label="파트너 검색"
            style={{
              flex: 1, height: '52px', padding: '0 18px', boxSizing: 'border-box',
              border: `1px solid ${C.g200}`, borderRadius: '12px', outline: 'none',
              fontFamily: 'Pretendard, sans-serif', fontSize: '15px',
            }}
          />
          <button
            onClick={handleSearch}
            style={{
              height: '52px', padding: '0 24px', borderRadius: '12px', border: 'none',
              background: C.bk, cursor: 'pointer', ...t('15px', 600, C.white),
            }}
          >
            검색
          </button>
        </div>

        {error && <p style={t('14px', 500, '#EF4444')}>{error}</p>}

        {unavailable ? (
          <div style={{
            padding: '60px 24px', textAlign: 'center',
            border: `1px solid ${C.g200}`, borderRadius: '14px', background: C.bg,
          }}>
            <span style={t('16px', 400, C.sub)}>파트너 정보를 준비 중입니다.</span>
          </div>
        ) : loading ? (
          <div style={{ padding: '80px 0', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size="lg" />
          </div>
        ) : partners.length === 0 ? (
          <div style={{
            padding: '60px 24px', textAlign: 'center',
            border: `1px solid ${C.g200}`, borderRadius: '14px', background: C.bg,
          }}>
            <span style={t('16px', 400, C.sub)}>
              {termRef.current ? '검색 결과가 없습니다.' : '등록된 파트너가 없습니다.'}
            </span>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '20px',
          }}>
            {partners.map(p => (
              <PartnerCard
                key={p.id}
                partner={p}
                onClick={() => navigate(`/partners/${p.slug ?? p.id}`)}
              />
            ))}
          </div>
        )}

        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
            <button
              onClick={() => fetchPartners(type, termRef.current, page + 1, true)}
              disabled={loadingMore}
              style={{
                padding: '14px 32px', borderRadius: '12px', cursor: loadingMore ? 'default' : 'pointer',
                border: `1px solid ${C.g200}`, background: C.white, ...t('15px', 600, C.bk),
                display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '52px',
              }}
            >
              {loadingMore
                ? <LoadingSpinner size="sm" style={{ width: 22, height: 22 }} />
                : `더 보기 (${partners.length}/${total})`}
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
